import { createStore } from "vuex";
import Axios from "axios";
import { API_PATH, IOT_API_PATH } from "../constants";

export const store = createStore({
  state: {
    authToken: "",
    authIotFlagToken: "",
    rules: [],
    iotRules: [],
    waitingForServiceCall: false,
    errorOnIoTRule: false,
    rulesResultsPending: false,
    iotRulesResultsPending: false,
  },
  mutations: {
    UPDATE_AUTH_TOKEN(state, token) {
      state.authToken = token;
    },
    UPDATE_IOT_FLAG_AUTH_TOKEN(state, token) {
      state.authIotFlagToken = token;
    },
    RULES(state, rules) {
      state.rules = rules;
    },
    IOT_RULES(state, rules) {
      state.iotRules = rules;
    },
    WAITING_FOR_SERVICE_CALL(state, value) {
      state.waitingForServiceCall = value;
    },
    ERROR_ON_IOT_RULE(state, value) {
      state.errorOnIoTRule = value;
    },
    RULES_RESULTS_PENDING(state, value) {
      state.rulesResultsPending = value;
    },
    IOT_RULES_RESULTS_PENDING(state, value) {
      state.iotRulesResultsPending = value;
    },
  },
  actions: {
    async FETCH_RULES(state, { router }) {
      if (state.rulesResultsPending) {
        return;
      }
      this.commit("RULES_RESULTS_PENDING", true);
      this.commit("RULES", []);

      return Axios.get(API_PATH, {
        headers: createHeader(),
        crossDomain: true,
      })
        .then((response) => {
          this.commit("RULES", response.data);
        })
        .catch((err) => {
          console.error("Rules fetch failed", err);
          router.push("/auth");
        })
        .finally(() => {
          this.commit("RULES_RESULTS_PENDING", false);
        });
    },

    async ADD_RULE(state, newAddRule) {
      this.commit("WAITING_FOR_SERVICE_CALL", true);

      const responseBody = {
        rule: newAddRule,
      };

      Axios.post(API_PATH, responseBody, { headers: createHeader() })
        .catch((err) => {
          console.error("Create rule failed", err);
          if (
            err.response &&
            err.response.status >= 400 &&
            err.response.status < 500
          ) {
            this.$router.push("/auth");
          }
        })
        .finally(() => {
          this.commit("WAITING_FOR_SERVICE_CALL", false);
        });
    },
    async UPDATE_RULE(state, updateRule) {
      this.commit("WAITING_FOR_SERVICE_CALL", true);

      Axios.put(
        API_PATH,
        {
          rule: updateRule,
        },
        { headers: createHeader() },
      )
        .catch((err) => {
          console.error("Rules update failed", err);
          if (
            err.response &&
            err.response.status >= 400 &&
            err.response.status < 500
          ) {
            this.$router.push("/auth");
          }
        })
        .finally(() => {
          this.commit("WAITING_FOR_SERVICE_CALL", false);
        });
    },
    async DELETE_RULE(state, deleteRule) {
      this.commit("WAITING_FOR_SERVICE_CALL", true);

      Axios.delete(
        API_PATH + "uuid/" + deleteRule.uuid + "/chip/" + deleteRule.chip,
        {
          headers: createHeader(),
        },
      )
        .catch((err) => {
          console.error("Delete rule failed", err);
          if (
            err.response &&
            err.response.status >= 400 &&
            err.response.status < 500
          ) {
            this.$router.push("/auth");
          }
        })
        .finally(() => {
          this.commit("WAITING_FOR_SERVICE_CALL", false);
        });
    },
    async FETCH_IOT_RULES(state, { router }) {
      if (state.iotRulesResultsPending) {
        return;
      }
      this.commit("IOT_RULES_RESULTS_PENDING", true);
      this.commit("IOT_RULES", []);

      return requestRules(this, router);
    },

    async ADD_IOT_RULE(state, newAddRule) {
      this.commit("ERROR_ON_IOT_RULE", false);
      this.commit("WAITING_FOR_SERVICE_CALL", true);
      const responseBody = { rule: parseIotRule(newAddRule) };

      Axios.post(IOT_API_PATH, responseBody, {
        headers: createHeader(store.state.authIotFlagToken),
        crossDomain: true,
      })
        .catch((err) => {
          console.error("Create IOT rule failed", err.response.data);
          this.commit("ERROR_ON_IOT_RULE", err.response.data);
          // Disappear error after 15 seconds
          setTimeout(() => {
            this.commit("ERROR_ON_IOT_RULE", false);
          }, 15000);
        })
        .finally(() => {
          this.commit("WAITING_FOR_SERVICE_CALL", false);
        });
    },
    async UPDATE_IOT_RULE(state, updateRule) {
      this.commit("WAITING_FOR_SERVICE_CALL", true);

      Axios.put(
        IOT_API_PATH,
        { rule: parseIotRule(updateRule) },
        { headers: createHeader(store.state.authIotFlagToken) },
      )
        .catch((err) => {
          console.error("EDIT IOT rule failed", err.response.data);
          this.commit("ERROR_ON_IOT_RULE", err.response.data);
          // Disappear error after 15 seconds
          setTimeout(() => {
            this.commit("ERROR_ON_IOT_RULE", false);
          }, 15000);
        })
        .finally(() => {
          this.commit("WAITING_FOR_SERVICE_CALL", false);
        });
    },
    async DELETE_IOT_RULE(state, deleteIoTRule) {
      this.commit("WAITING_FOR_SERVICE_CALL", true);

      Axios.delete(
        IOT_API_PATH +
          "uuid/" +
          deleteIoTRule.uuid +
          "/chip/" +
          deleteIoTRule.chip,
        { headers: createHeader(store.state.authIotFlagToken) },
      )
        .catch((err) => {
          console.error("Delete IOT rule failed", err.response.data);
          this.commit("ERROR_ON_IOT_RULE", err.response.data);
          // Disappear error after 15 seconds
          setTimeout(() => {
            this.commit("ERROR_ON_IOT_RULE", false);
          }, 15000);
        })
        .finally(() => {
          this.commit("WAITING_FOR_SERVICE_CALL", false);
        });
    },
  },
});

function _parseIotRules(data) {
  return data.map((item) => {
    return { ...item, shadowPayload: JSON.stringify(item.shadowPayload) };
  });
}

function parseIotRule(iotRule) {
  return { ...iotRule, shadowPayload: JSON.parse(iotRule.shadowPayload) };
}

function requestRules(that, router) {
  return Axios.get(IOT_API_PATH, {
    headers: createHeader(store.state.authIotFlagToken),
    crossDomain: true,
  })
    .then((response) => {
      that.commit("IOT_RULES", _parseIotRules(response.data));
    })
    .catch((err) => {
      console.error("IoT Rules fetch failed", err);
      router.push("/auth");
    })
    .finally(() => {
      that.commit("IOT_RULES_RESULTS_PENDING", false);
    });
}

function createHeader(token = null) {
  const authToken = token || store.state.authToken;
  return {
    Authorization: authToken,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}
